import { useMemo, useEffect, useState } from 'react';

interface StudySummaryProps {
  stats: {
    monthlyPosts: number;
    monthlyHours: number;
    weeklyPosts: number;
    totalPosts: number;
    totalHours: number;
  };
  dailyData: { date: string; hours: number }[];
  subjectData: { name: string; value: number }[];
  layoutMode?: 'normal' | 'compact';
}

export default function StudySummary({ stats, dailyData, subjectData, layoutMode = 'normal' }: StudySummaryProps) {
  // 現在日付の状態管理（日付変更を検知するため）
  const [currentDate, setCurrentDate] = useState(new Date());

  // undefinedガード
  const safeDailyData = dailyData ?? [];
  const safeSubjectData = subjectData ?? [];
  const safeStats = stats ?? { monthlyPosts: 0, monthlyHours: 0, weeklyPosts: 0, totalPosts: 0, totalHours: 0 };

  // 日付変更を検知して自動更新
  useEffect(() => {
    const checkDateChange = () => {
      const now = new Date();
      if (now.getDate() !== currentDate.getDate() || 
          now.getMonth() !== currentDate.getMonth() || 
          now.getFullYear() !== currentDate.getFullYear()) {
        setCurrentDate(now);
      }
    };

    // 1分ごとに日付変更をチェック
    const interval = setInterval(checkDateChange, 60000);
    return () => clearInterval(interval);
  }, [currentDate]);

  // 集計期間の計算
  const periods = useMemo(() => {
    const now = new Date(currentDate);
    
    // 今週の開始日（月曜日）と終了日（日曜日）を計算
    const dayOfWeek = now.getDay(); // 0=日曜, 1=月曜, ..., 6=土曜
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // 月曜日までの日数差
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() + diffToMonday);
    weekStart.setHours(0, 0, 0, 0);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    
    // 今月の開始日と終了日
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // 日付フォーマット関数
    const formatDate = (date: Date) => {
      return `${date.getMonth() + 1}/${date.getDate()}`;
    };
    
    const formatMonth = (date: Date) => {
      return `${date.getFullYear()}年${date.getMonth() + 1}月`;
    };
    
    return {
      week: {
        start: weekStart,
        end: weekEnd,
        label: `${formatDate(weekStart)}〜${formatDate(weekEnd)}`,
      },
      month: {
        start: monthStart,
        end: monthEnd,
        label: `${formatMonth(monthStart)}（${formatDate(monthStart)}〜${formatDate(monthEnd)}）`,
      },
    };
  }, [currentDate]);

  // 今週・今月の統計計算
  const summary = useMemo(() => {
    // 今週の学習時間（直近7日）
    const thisWeekHours = safeDailyData.slice(-7).reduce((sum, d) => sum + d.hours, 0);
    
    // 先週の学習時間（8-14日前）
    const lastWeekHours = safeDailyData.slice(-14, -7).reduce((sum, d) => sum + d.hours, 0);
    
    // 今月の学習時間
    const thisMonthHours = safeStats.monthlyHours;
    
    // 先月の学習時間（仮想データ：実際はSupabaseから取得）
    const lastMonthHours = thisMonthHours * 0.85; // 仮の比較値
    
    // 今週の投稿数
    const thisWeekPosts = safeStats.weeklyPosts;
    
    // 先週の投稿数（仮想データ）
    const lastWeekPosts = Math.max(0, thisWeekPosts - 2);
    
    // 今月の投稿数
    const thisMonthPosts = safeStats.monthlyPosts;
    
    // 先月の投稿数（仮想データ）
    const lastMonthPosts = Math.max(0, thisMonthPosts - 3);
    
    // 平均学習時間/日
    const avgHoursPerDay = thisWeekHours / 7;
    const lastAvgHoursPerDay = lastWeekHours / 7;
    
    // 増減率計算
    const weekHoursChange = lastWeekHours > 0 ? ((thisWeekHours - lastWeekHours) / lastWeekHours) * 100 : 0;
    const monthHoursChange = lastMonthHours > 0 ? ((thisMonthHours - lastMonthHours) / lastMonthHours) * 100 : 0;
    const weekPostsChange = lastWeekPosts > 0 ? ((thisWeekPosts - lastWeekPosts) / lastWeekPosts) * 100 : 0;
    const monthPostsChange = lastMonthPosts > 0 ? ((thisMonthPosts - lastMonthPosts) / lastMonthPosts) * 100 : 0;
    const avgChange = lastAvgHoursPerDay > 0 ? ((avgHoursPerDay - lastAvgHoursPerDay) / lastAvgHoursPerDay) * 100 : 0;
    
    // 最も学習した科目TOP3
    const topSubjects = safeSubjectData.slice(0, 3);
    
    // 学習達成率（目標：週20時間、月80時間と仮定）
    const weeklyGoal = 20;
    const monthlyGoal = 80;
    const weekProgress = Math.min((thisWeekHours / weeklyGoal) * 100, 100);
    const monthProgress = Math.min((thisMonthHours / monthlyGoal) * 100, 100);
    
    return {
      thisWeekHours: Math.round(thisWeekHours * 10) / 10,
      thisMonthHours: Math.round(thisMonthHours * 10) / 10,
      thisWeekPosts,
      thisMonthPosts,
      avgHoursPerDay: Math.round(avgHoursPerDay * 10) / 10,
      weekHoursChange: Math.round(weekHoursChange),
      monthHoursChange: Math.round(monthHoursChange),
      weekPostsChange: Math.round(weekPostsChange),
      monthPostsChange: Math.round(monthPostsChange),
      avgChange: Math.round(avgChange),
      topSubjects,
      weekProgress: Math.round(weekProgress),
      monthProgress: Math.round(monthProgress),
      weeklyGoal,
      monthlyGoal,
    };
  }, [safeStats, safeDailyData, safeSubjectData]);

  // 変化率表示コンポーネント
  const ChangeIndicator = ({ change }: { change: number }) => {
    if (change === 0) return <span className="text-xs text-gray-500">±0%</span>;
    const isPositive = change > 0;
    return (
      <span className={`text-xs font-medium ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
        <i className={`${isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-sm`}></i>
        {Math.abs(change)}%
      </span>
    );
  };

  const cardPadding = layoutMode === 'compact' ? 'p-3 sm:p-4' : 'p-4 sm:p-6';
  const titleSize = layoutMode === 'compact' ? 'text-base sm:text-lg' : 'text-lg sm:text-xl';
  const valueSize = layoutMode === 'compact' ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl';

  return (
    <section className={`${layoutMode === 'compact' ? 'py-3 sm:py-4' : 'py-4 sm:py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* ヘッダー */}
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-md">
            <i className="ri-dashboard-line text-xl sm:text-2xl text-white"></i>
          </div>
          <div>
            <h2 className={`font-bold text-gray-800 dark:text-white ${titleSize}`}>学習サマリー</h2>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              今週: {periods.week.label} | 今月: {periods.month.label}
            </p>
          </div>
        </div>

        {/* メイン統計カード */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${layoutMode === 'compact' ? 'gap-3' : 'gap-4 sm:gap-6'} mb-4 sm:mb-6`}>
          {/* 今週の学習時間 */}
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${cardPadding}`}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/30 rounded-lg">
                  <i className="ri-calendar-line text-base text-teal-600 dark:text-teal-400"></i>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">今週の学習時間</span>
              </div>
              <ChangeIndicator change={summary.weekHoursChange} />
            </div>
            <p className={`font-bold text-gray-800 dark:text-white ${valueSize}`}>
              {summary.thisWeekHours}
              <span className="text-sm sm:text-base font-normal text-gray-500 dark:text-gray-400 ml-1">時間</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{periods.week.label}</p>
          </div>

          {/* 今月の学習時間 */}
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${cardPadding}`}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                  <i className="ri-calendar-2-line text-base text-purple-600 dark:text-purple-400"></i>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">今月の学習時間</span>
              </div>
              <ChangeIndicator change={summary.monthHoursChange} />
            </div>
            <p className={`font-bold text-gray-800 dark:text-white ${valueSize}`}>
              {summary.thisMonthHours}
              <span className="text-sm sm:text-base font-normal text-gray-500 dark:text-gray-400 ml-1">時間</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{periods.month.label}</p>
          </div>

          {/* 平均学習時間/日 */}
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${cardPadding}`}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                  <i className="ri-time-line text-base text-emerald-600 dark:text-emerald-400"></i>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">平均学習時間/日</span>
              </div>
              <ChangeIndicator change={summary.avgChange} />
            </div>
            <p className={`font-bold text-gray-800 dark:text-white ${valueSize}`}>
              {summary.avgHoursPerDay}
              <span className="text-sm sm:text-base font-normal text-gray-500 dark:text-gray-400 ml-1">時間</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">直近7日間</p>
          </div>
        </div>

        {/* サブ統計 & TOP3科目 */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 ${layoutMode === 'compact' ? 'gap-3' : 'gap-4 sm:gap-6'}`}>
          {/* 投稿数統計 */}
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${cardPadding}`}>
            <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4">投稿数統計</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-article-line text-teal-600 dark:text-teal-400"></i>
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">今週の投稿</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base sm:text-lg font-bold text-gray-800 dark:text-white">{summary.thisWeekPosts}件</span>
                  <ChangeIndicator change={summary.weekPostsChange} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-file-list-line text-purple-600 dark:text-purple-400"></i>
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">今月の投稿</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base sm:text-lg font-bold text-gray-800 dark:text-white">{summary.thisMonthPosts}件</span>
                  <ChangeIndicator change={summary.monthPostsChange} />
                </div>
              </div>
            </div>
          </div>

          {/* 最も学習した科目TOP3 */}
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${cardPadding}`}>
            <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4">最も学習した科目 TOP3</h3>
            {summary.topSubjects.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {summary.topSubjects.map((subject, index) => (
                  <div key={subject.name} className="flex items-center gap-2 sm:gap-3">
                    <div className={`w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg font-bold text-xs sm:text-sm ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      index === 1 ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' :
                      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-800 dark:text-white truncate">{subject.name}</p>
                    </div>
                    <span className="text-sm sm:text-base font-bold text-teal-600 dark:text-teal-400 whitespace-nowrap">
                      {subject.value}h
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center py-4">データがありません</p>
            )}
          </div>
        </div>

        {/* 学習達成率 */}
        <div className={`bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl shadow-sm border border-indigo-200 dark:border-indigo-800 ${cardPadding} mt-4 sm:mt-6`}>
          <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
            <i className="ri-trophy-line text-indigo-600 dark:text-indigo-400"></i>
            学習達成率
          </h3>
          <div className="space-y-3 sm:space-y-4">
            {/* 週次目標 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">週次目標（{summary.weeklyGoal}時間）</span>
                <span className="text-xs sm:text-sm font-bold text-indigo-600 dark:text-indigo-400">{summary.weekProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 sm:h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-teal-500 to-emerald-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${summary.weekProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {summary.thisWeekHours} / {summary.weeklyGoal} 時間達成 ({periods.week.label})
              </p>
            </div>

            {/* 月次目標 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">月次目標（{summary.monthlyGoal}時間）</span>
                <span className="text-xs sm:text-sm font-bold text-purple-600 dark:text-purple-400">{summary.monthProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 sm:h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${summary.monthProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {summary.thisMonthHours} / {summary.monthlyGoal} 時間達成 ({periods.month.label})
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}